'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Shield, Sparkles } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const DEMO_CREDENTIALS = { email: 'maya.chen@runway.exe', password: 'runway2026!' };

export default function AuthPanel() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { name: '', email: '', password: '', confirmPassword: '', terms: false } });

  const fillDemo = () => {
    loginForm.setValue('email', DEMO_CREDENTIALS.email);
    loginForm.setValue('password', DEMO_CREDENTIALS.password);
    toast.success('Demo credentials filled — tap Sign In');
  };

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password, data.remember);
      toast.success('Welcome back 🌿');
      router.push('/runway-dashboard');
    } catch (error: any) {
      toast.error(error?.message || 'Invalid credentials — please try again');
      setIsLoading(false);
    }
  };

  const handleSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.name });
      toast.success('Account created — let\'s set up your runway 🚀');
      router.push('/financial-onboarding');
    } catch (error: any) {
      toast.error(error?.message || 'Could not create account — please try again');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#F5F0E8' }}>
      {/* Left Panel — Brand */}
      <div
        className="hidden lg:flex flex-col justify-between w-[52%] relative overflow-hidden p-12"
        style={{ background: 'linear-gradient(145deg, #3D2B1F 0%, #6B4F3A 40%, #A85A38 100%)' }}
      >
        {/* Decorative circles */}
        <div className="absolute top-[-80px] right-[-80px] w-[320px] h-[320px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #D4896A, transparent)' }} />
        <div className="absolute bottom-[-60px] left-[-60px] w-[260px] h-[260px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #7A9E7E, transparent)' }} />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} className="text-cream" />
          <span className="font-display text-2xl text-cream tracking-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Runway.exe
          </span>
        </div>

        {/* Main copy */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ background: 'rgba(196,113,74,0.25)', color: '#D4896A', border: '1px solid rgba(196,113,74,0.3)' }}>
              <Sparkles size={12} />
              Financial clarity for your next chapter
            </div>
            <h1
              className="text-4xl xl:text-5xl text-cream leading-tight"
              style={{ fontFamily: 'DM Serif Display, serif' }}
            >
              You didn't lose<br />
              <em>your future.</em><br />
              Just your job.
            </h1>
            <p className="text-cream/70 text-base leading-relaxed max-w-sm">
              Runway.exe helps you see exactly how long your savings will carry you — with clarity, not panic. Because knowing is always better than guessing.
            </p>
          </div>

          {/* Testimonial */}
          <div className="p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <p className="text-cream/80 text-sm leading-relaxed italic">
              "I was spiraling for two weeks before I found Runway. Seeing '11 months' in big numbers on my screen made me exhale for the first time since the layoff."
            </p>
            <div className="flex items-center gap-3 mt-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-cream"
                style={{ background: '#C4714A' }}>
                SR
              </div>
              <div>
                <p className="text-cream text-xs font-semibold">Sofia R.</p>
                <p className="text-cream/50 text-xs">Former Product Manager, NYC</p>
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex items-center gap-4">
            {[
              { icon: <Shield size={14} />, label: 'Bank-grade encryption' },
              { icon: <Lock size={14} />, label: 'Zero data sharing' },
            ].map((b) => (
              <div key={`badge-${b.label}`} className="flex items-center gap-1.5 text-cream/60 text-xs">
                {b.icon}
                <span>{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <p className="text-cream/30 text-xs relative z-10">
          © 2026 Runway.exe · Built with care for people in transition
        </p>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 relative">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8 self-start">
          <AppLogo size={32} className="text-warmBrown" />
          <span className="font-display text-xl text-warmBrown" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Runway.exe
          </span>
        </div>

        <div className="w-full max-w-md">
          {/* Tab switcher */}
          <div className="flex bg-taupe rounded-2xl p-1 mb-8">
            {(['login', 'signup'] as const).map((tab) => (
              <button
                key={`tab-${tab}`}
                onClick={() => { setMode(tab); setIsLoading(false); }}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  mode === tab
                    ? 'bg-white text-warmBrown shadow-card'
                    : 'text-warmBrown/50 hover:text-warmBrown/80'
                }`}
              >
                {tab === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {mode === 'login' ? (
            <div className="animate-slide-up">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-warmBrown" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  Welcome back
                </h2>
                <p className="text-warmBrown/60 text-sm mt-1">Your runway is waiting for you.</p>
              </div>

              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-warmBrown mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40" />
                    <input
                      type="email"
                      {...loginForm.register('email', { required: 'Email is required' })}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm text-warmBrown placeholder-warmBrown/30 transition-all focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      style={{ background: '#FDFAF6', borderColor: '#E8E0D0' }}
                    />
                  </div>
                  {loginForm.formState.errors.email && (
                    <p className="text-red-500 text-xs mt-1">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-semibold text-warmBrown">Password</label>
                    <button type="button" className="text-xs text-terracotta hover:underline font-medium">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...loginForm.register('password', { required: 'Password is required' })}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 rounded-xl border text-sm text-warmBrown placeholder-warmBrown/30 transition-all focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      style={{ background: '#FDFAF6', borderColor: '#E8E0D0' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40 hover:text-warmBrown/70 transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-red-500 text-xs mt-1">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                {/* Remember me */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="remember"
                    {...loginForm.register('remember')}
                    className="w-4 h-4 rounded accent-terracotta"
                  />
                  <label htmlFor="remember" className="text-sm text-warmBrown/60">Keep me signed in</label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70"
                  style={{ background: 'linear-gradient(135deg, #C4714A, #A85A38)' }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                        <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    <>Sign In <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-taupe" />
                <span className="text-xs text-warmBrown/40 font-medium">or continue with</span>
                <div className="flex-1 h-px bg-taupe" />
              </div>

              {/* Social buttons */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'Google', icon: 'G' },
                  { name: 'Apple', icon: '🍎' },
                ].map((s) => (
                  <button
                    key={`social-${s.name}`}
                    type="button"
                    className="flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium text-warmBrown hover:bg-taupe/60 transition-all duration-150 active:scale-95"
                    style={{ borderColor: '#E8E0D0', background: '#FDFAF6' }}
                    onClick={() => toast.info(`${s.name} auth coming soon`)}
                  >
                    <span className="text-base">{s.icon}</span>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="animate-slide-up">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-warmBrown" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  You've got this
                </h2>
                <p className="text-warmBrown/60 text-sm mt-1">Let's figure out your runway together.</p>
              </div>

              <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-warmBrown mb-1.5">Full name</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40" />
                    <input
                      type="text"
                      {...signupForm.register('name', { required: 'Name is required' })}
                      placeholder="Your name"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm text-warmBrown placeholder-warmBrown/30 transition-all focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      style={{ background: '#FDFAF6', borderColor: '#E8E0D0' }}
                    />
                  </div>
                  {signupForm.formState.errors.name && (
                    <p className="text-red-500 text-xs mt-1">{signupForm.formState.errors.name.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-warmBrown mb-1.5">Email address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40" />
                    <input
                      type="email"
                      {...signupForm.register('email', { required: 'Email is required' })}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm text-warmBrown placeholder-warmBrown/30 transition-all focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      style={{ background: '#FDFAF6', borderColor: '#E8E0D0' }}
                    />
                  </div>
                  {signupForm.formState.errors.email && (
                    <p className="text-red-500 text-xs mt-1">{signupForm.formState.errors.email.message}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-semibold text-warmBrown mb-1.5">Create a password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...signupForm.register('password', { required: 'Password required', minLength: { value: 8, message: 'Min 8 characters' } })}
                      placeholder="Min. 8 characters"
                      className="w-full pl-10 pr-10 py-3 rounded-xl border text-sm text-warmBrown placeholder-warmBrown/30 transition-all focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      style={{ background: '#FDFAF6', borderColor: '#E8E0D0' }}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40 hover:text-warmBrown/70 transition-colors">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="text-red-500 text-xs mt-1">{signupForm.formState.errors.password.message}</p>
                  )}
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-sm font-semibold text-warmBrown mb-1.5">Confirm password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40" />
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 rounded-xl border text-sm text-warmBrown placeholder-warmBrown/30 transition-all focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      style={{ background: '#FDFAF6', borderColor: '#E8E0D0' }}
                    />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-warmBrown/40 hover:text-warmBrown/70 transition-colors">
                      {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Terms */}
                <div className="flex items-start gap-2">
                  <input type="checkbox" id="terms" {...signupForm.register('terms', { required: 'You must accept the terms' })}
                    className="w-4 h-4 rounded mt-0.5 accent-terracotta" />
                  <label htmlFor="terms" className="text-xs text-warmBrown/60 leading-relaxed">
                    I agree to the{' '}
                    <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline font-medium">Terms of Service</Link>
                    {' '}and{' '}
                    <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline font-medium">Privacy Policy</Link>
                  </label>
                </div>
                {signupForm.formState.errors.terms && (
                  <p className="text-red-500 text-xs">{signupForm.formState.errors.terms.message}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-all duration-150 active:scale-95 disabled:opacity-70"
                  style={{ background: 'linear-gradient(135deg, #C4714A, #A85A38)' }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                        <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating account...
                    </>
                  ) : (
                    <>Start my runway <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}